import type { Phone } from '../types'

interface PhoneMultiSelectProps {
  /**
   * Full phones list (admin sees all). Filtering to "unattached" is done
   * inside — caller only supplies the selection state.
   */
  phones: Phone[]
  /** Ids that should survive the filter even if they're attached (edit mode). */
  keepIds?: string[]
  /** Current selection. */
  selected: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
}

/**
 * Multi-select checkbox list for access-key phone attachment. Filters to
 * unattached phones client-side so there's no new `/phones?unattached=1`
 * endpoint — the plan explicitly calls this out as a DRY win.
 *
 * `keepIds` is how edit-mode preserves currently-attached phones (whose
 * `userId` points at the current user, not null).
 */
export function PhoneMultiSelect({
  phones, keepIds = [], selected, onChange, disabled,
}: PhoneMultiSelectProps) {
  const keep = new Set(keepIds)
  const candidates = phones.filter(p => p.userId === null || keep.has(p.id))

  const toggle = (id: string) => {
    if (disabled) return
    onChange(
      selected.includes(id)
        ? selected.filter(x => x !== id)
        : [...selected, id],
    )
  }

  if (candidates.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        Нет свободных трубок. Отвяжите трубку от другого ключа или создайте новую в разделе «Phones».
      </p>
    )
  }

  return (
    <div
      role="group"
      aria-label="Выбор трубок"
      className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800"
    >
      {candidates.map(phone => {
        const checked = selected.includes(phone.id)
        return (
          <label
            key={phone.id}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm ${
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(phone.id)}
              disabled={disabled}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span className="flex-1 text-gray-900 dark:text-gray-100">{phone.name}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{phone.sim}</span>
          </label>
        )
      })}
    </div>
  )
}
